import {
  MantineProvider as BaseMantineProvider,
  type MantineProviderProps,
  type DefaultMantineColor,
  type Tuple,
} from '@mantine/core'
import { ModalsProvider } from '@mantine/modals'
import { Notifications } from '@mantine/notifications'
import { type FC } from 'react'
import { dsmTokens } from '@app/components/ui/tokens/dsmTokens'

type ExtendedCustomColors =
  | 'brandBlue'
  | 'brandGreen'
  | 'brandOrange'
  | 'brandPink'
  | 'brandYellow'
  | 'brandGreenCustom'
  | 'brandBlueGrey'
  | 'brandTeal'
  | 'transparent'
  | DefaultMantineColor

declare module '@mantine/core' {
  // noinspection JSUnusedGlobalSymbols
  export interface MantineThemeColorsOverride {
    colors: Record<ExtendedCustomColors, Tuple<string, 10>>
  }
}

export const MantineProvider: FC<MantineProviderProps> = ({ children }) => (
  <BaseMantineProvider
    withGlobalStyles
    withNormalizeCSS
    theme={{
      fontFamily: 'Metropolis, sans-serif',
      // DEV-1927: the DSM colors the Overview board reads, the same object Konva shapes import
      other: { dsm: dsmTokens },
      colors: {
        brandPrimary: ['#41BEB2'],
        brandSecondary: ['#F6A246'],
        // Original brand colors were fed into https://smart-swatch.netlify.app/
        // Original brand color: #3FBFB8
        brandBlue: [
          '#defafc',
          '#bee9ee',
          '#9bdce0',
          '#76d2d3',
          '#52c6c4',
          '#39ada6',
          '#288586',
          '#195b61',
          '#05343b',
          '#4175BE',
        ],
        // Original brand color: #ABD37C
        brandGreen: [
          '#eefbe4',
          '#d6edc3',
          '#bfdf9f',
          '#a9d279',
          '#8ac554',
          '#69ac3b',
          '#4b862d',
          '#305f1f',
          '#193910',
          '#011400',
        ],

        brandGreenCustom: [
          'rgba(65, 190, 178, 0.05)', // 0 - 5% opacity (lightest)
          '#DAF4F2', // 1 - 10% opacity
          'rgba(65, 190, 178, 0.2)', // 2 - 20% opacity
          'rgba(65, 190, 178, 0.3)', // 3 - 30% opacity
          'rgba(65, 190, 178, 0.4)', // 4 - 40% opacity
          'rgba(65, 190, 178, 0.5)', // 5 - 50% opacity
          'rgba(65, 190, 178, 0.6)', // 6 - 60% opacity
          'rgba(65, 190, 178, 0.7)', // 7 - 70% opacity
          'rgba(65, 190, 178, 0.8)', // 8 - 80% opacity
          '#41BEB2', // 9 - main color (100%)
        ],
        brandTeal: [
          '#e6faf7',
          '#b3f0e8',
          '#80e6da',
          '#4ddbcb',
          '#41BEB2',
          '#41BEB2',
          '#41BEB2',
          '#41BEB2',
          '#16544f',
          '#0c3834',
        ],
        // #F7A245
        brandOrange: [
          '#fff2de',
          '#fed9b2',
          '#fbc284',
          '#f8a954',
          '#f59125',
          '#db770d',
          '#ab5d07',
          '#7a4203',
          '#4a2700',
          '#1d0b00',
        ],
        // Original brand color: #F170A6
        brandPink: [
          '#ffe5f6',
          '#fbb7d9',
          '#f58abb',
          '#ef5c9a',
          '#ea3089',
          '#d0177c',
          '#a3106a',
          '#750854',
          '#480338',
          '#1d0018',
        ],
        // Original brand color: #F2E10B
        brandYellow: [
          '#fffddc',
          '#fcf7b0',
          '#f9f182',
          '#f7eb52',
          '#f5e623',
          '#dccc0a',
          '#ab9f03',
          '#7a7100',
          '#494400',
          '#191700',
        ],
        brandBlueGrey: [
          '#f5f6f8', // Lightest shade
          '#e8ebef',
          '#dbe0e6',
          '#ced5dd',
          '#c1cad4',
          '#b4bfcb',
          '#a7b4c2',
          '#9aa9b9',
          '#8d9eb0',
          '#9DB3C9', // Main color (index 9)
        ],
        transparent: [
          '#00000005',
          '#00000010',
          '#00000015',
          '#00000020',
          '#00000025',
          '#00000030',
          '#00000035',
          '#00000040',
          '#00000045',
          '#00000050',
        ],
      },
      loader: 'bars',
      primaryColor: 'blue',
      primaryShade: {
        light: 4,
        dark: 8,
      },
      // Global component style overrides
      components: {
        Switch: {
          styles: (theme) => ({
            track: {
              // Ensure pointer cursor for the visible track
              cursor: 'pointer',
            },
            thumb: {
              // Ensure pointer cursor for the thumb/knob
              cursor: 'pointer',
            },
            input: {
              // Ensure pointer when focusing or hovering the input itself
              cursor: 'pointer',
            },
          }),
        },
        Image: {
          styles: () => ({
            root: {
              cursor: 'pointer',
            },
            image: {
              cursor: 'pointer',
            },
          }),
        },
        Checkbox: {
          styles: () => ({
            root: {
              cursor: 'pointer',
            },
            input: {
              cursor: 'pointer',
            },
            label: {
              cursor: 'pointer',
            },
          }),
        },
        Radio: {
          styles: () => ({
            root: {
              cursor: 'pointer',
            },
            radio: {
              cursor: 'pointer',
            },
            input: {
              cursor: 'pointer',
            },
            label: {
              cursor: 'pointer',
            },
          }),
        },
      },
      globalStyles: () => ({
        'html, body': {
          overscrollBehaviorX: 'none',
        },
      }),
    }}
  >
    <ModalsProvider>
      <Notifications
        position='bottom-center'
        zIndex={7000000}
      />
      {children}
    </ModalsProvider>
  </BaseMantineProvider>
)
